import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { customRange } from '@next/arable-lib/shared';
import { CoreActions } from '@next/core-lib';
import { DialogService } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { RelationshipsActions } from '@next/core-lib/relationships';
import { SessionActions } from '@next/core-lib/session';
import { UserActions } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Environment } from 'src/environments/environment';

import {
  AppActionTypes,
  ContextChanged,
  CropYearChanged,
  DisableGrainRegistrationReminder,
  GetCompanySettings,
  GetCropFieldAdvices,
  GetCrops,
  GetDefaultCropYear,
  GetFertilizationTableReport,
  GetLoggedInEmployeeData,
  GetNonProductiveCrops,
  GetSoilTypes,
  LoadCustomerData,
  LoadCustomerProgress,
  LoadCustomerProgressSuccess,
  SetAvailableCropYears,
  SetCompanySettings,
  SetCropFieldAdvices,
  SetCrops,
  SetDefaultCropYear,
  SetEnvironmentalImpactReport,
  SetLoggedInEmployeeData,
  SetNonProductiveCrops,
  SetPlanetProofReport,
  SetReportSort,
  SetSkalReport,
  SetSoilTypes,
  ShowGrainRegistrationReminder,
  ShowIndex,
  UpdateGrasslandUsages,
} from './actions';
import {
  getAvailableCropYears,
  getCropYear,
  getDefaultCropYear,
  getReportSort,
  selectCropFieldAdvices,
  selectCustomerProgress,
  selectSessionCustomerId,
} from './selectors';

import {
  AdviceActionTypes,
  ClearOrganicMatterReport,
  GetCustomerFertilizerStock,
  GetExecutedTreatmentForAdviceId,
  GetFertilizationSettings,
  GetRecommendedNutrientQuantityByAdviceIds,
  SetOrganicMatterReport,
} from '../advice/store/actions';
import {
  selectEditingAdvice,
  selectEditingTreatmentType,
  selectFertilizationSettings,
  selectOrganicMatterReport,
  selectSelectedCropFieldIds,
} from '../advice/store/selectors';
import { CropAidApiService } from '../crop-aid/services/crop-aid-api.service';
import * as LegalStandardActions from '../legal-standard/store/legal-standard.actions';
import { ReportSort, ReportSortOption } from '../reports/models';
import { TreatmentType } from '../sales-order-period/models/sales-order-period.models';
import {
  GetPastSalesOrderPeriods,
  GetSalesOrderPeriods,
} from '../sales-order-period/store/sales-order-period.actions';
import { GrainRegistrationReminderDialogComponent } from '../shared/components/grain-registration-reminder-dialog/grain-registration-reminder-dialog.component';
import { CropFieldAdvice, Employee, OrganicMatterReport, SiteState } from '../shared/models';
import { ApiService } from '../shared/services/api.service';
import { ArableCustomerService } from '../shared/services/arable-customer.service';
import { PeriodService } from '../shared/services/arable-period.service';
import { CropApiService } from '../shared/services/crop.service';
import { EnvironmentalImpactService } from '../shared/services/environmental-impact.service';
import { OrganicMatterService } from '../shared/services/organic-matter-service';
import { PlanetProofService } from '../shared/services/planet-proof.service';
import { ReportApiService } from '../shared/services/report.service';
import { SoilTypeApiService } from '../shared/services/soil-type-api.service';
import { TranslationHelpers } from '../shared/util/translationHelpers';

@Injectable()
export class AppEffects {
  protected readonly store = inject<Store<SiteState>>(Store);
  protected readonly actions$ = inject(Actions);
  protected readonly api = inject(ApiService);
  protected readonly router = inject(Router);
  protected readonly cropAidapi = inject(CropAidApiService);
  protected readonly reportApi = inject(ReportApiService);
  protected readonly organicMatterService = inject(OrganicMatterService);
  protected readonly planetProofService = inject(PlanetProofService);
  protected readonly cropApi = inject(CropApiService);
  protected readonly periodService = inject(PeriodService);
  protected readonly environmentalImpactService = inject(EnvironmentalImpactService);
  private readonly arableCustomerService = inject(ArableCustomerService);
  private readonly soilTypeApiService = inject(SoilTypeApiService);
  private readonly locale = inject(LocaleService);
  private readonly translationHelpers = inject(TranslationHelpers);
  private readonly dialog = inject(DialogService);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  userInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitialized),
      switchMap(() => [new GetDefaultCropYear(), new GetCrops(), new GetNonProductiveCrops()]),
    ),
  );

  getDefaultCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetDefaultCropYear),
      switchMap((_) =>
        this.periodService
          .getDefaultYear()
          .pipe(map((defaultYear: number) => new SetDefaultCropYear(defaultYear))),
      ),
    ),
  );

  setDefaultCropYear$ = createEffect(() => {
    const currentYear = new Date().getFullYear();
    const rangeStartYear = currentYear - 3;
    const rangeEndYear = currentYear + 1;
    const availableCropYears = customRange(rangeStartYear, rangeEndYear);
    return this.actions$.pipe(
      ofType(AppActionTypes.SetDefaultCropYear),
      switchMap((_) => [new SetAvailableCropYears(availableCropYears)]),
    );
  });

  customerInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerInitialized),
      withLatestFrom(this.store.pipe(select(selectSessionCustomerId))),
      switchMap(([_, customerId]) =>[
        new ContextChanged(),
        new SetReportSort(this.loadReportSort(customerId)),
      ],
      ),
    ),
  );

  customerChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerChanged),
      tap((_) => this.router.navigate(['/'])),
      withLatestFrom(this.store.pipe(select(selectSessionCustomerId))),
      switchMap(([_, customerId]) => [
        new ContextChanged(),
        new SetReportSort(this.loadReportSort(customerId)),
        new ClearOrganicMatterReport(),
      ]),
    ),
  );

  cropYearChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.CropYearChanged),
      map(() => new ContextChanged()),
    ),
  );

  contextChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ContextChanged),
      switchMap(() =>
        combineLatest([
          this.store.pipe(select(selectSessionCustomerId)),
          this.store.pipe(select(getCropYear)),
        ]).pipe(
          filter(([customerId, cropYear]) => !!customerId && !!cropYear),
          take(1),
          map(() => new LoadCustomerData()),
        ),
      ),
    ),
  );

  relationshipSelectedRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RelationshipsActions.setRelationshipSelectedRedirect),
      switchMap(({ redirectTo }) => {
        const parts = redirectTo ? redirectTo.split('/').slice(1) : [];
        if (parts[0] === 'advice' && parts.length > 2) {
          return of(RelationshipsActions.setRelationshipSelectedRedirect({ redirectTo: '/' }));
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  loadCustomerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LoadCustomerData),
      switchMap((_) => [
        new GetCropFieldAdvices(),
        new GetFertilizationSettings(),
        new GetLoggedInEmployeeData(),
        new GetSalesOrderPeriods(),
        new GetPastSalesOrderPeriods(),
        new LoadCustomerProgress(),
        new GetCustomerFertilizerStock(),
        new GetCompanySettings(),
        new GetSoilTypes(),
      ]),
    ),
  );

  getFertilizationLegalStandards$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(AppActionTypes.SetCropFieldAdvices)),
      this.actions$.pipe(ofType(AdviceActionTypes.SetFertilizationSettings)),
    ]).pipe(
      withLatestFrom(
        this.store.pipe(select(selectCropFieldAdvices)),
        this.store.pipe(select(selectFertilizationSettings)),
      ),
      filter(
        ([_, cropFieldAdvices, fertilizationSettings]) =>
          cropFieldAdvices.length > 0 &&
          fertilizationSettings.filter((f) =>
            cropFieldAdvices.map((a) => a.adviceId).includes(f.adviceId),
          ).length > 0,
      ),
      map(() => LegalStandardActions.getFertilizationLegalStandards()),
    ),
  );

  setCropYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SetCropYear),
      withLatestFrom(
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(getAvailableCropYears)),
        this.store.pipe(select(getDefaultCropYear)),
      ),
      switchMap(([{ cropYear }, currentCropYear, availableCropYears, defaultCropYear]) => {
        if (isNaN(cropYear) || availableCropYears.indexOf(cropYear) === -1) {
          return EMPTY;
        }

        if (cropYear !== currentCropYear) {
          return of(
            SessionActions.setSession({
              data:
                cropYear === defaultCropYear
                  ? { arable: {} }
                  : {
                    arable: {
                      cropYear,
                    },
                  },
            }),
            new CropYearChanged(cropYear),
          );
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  showIndex$: Observable<[ShowIndex, number[]]> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.ShowIndex),
        withLatestFrom(this.store.pipe(select(selectSelectedCropFieldIds))),
        tap(([_, cropFieldIds]: [ShowIndex, number[]]) => {
          this.router.navigate(
            cropFieldIds.length > 0 ? ['/cropfields/', JSON.stringify(cropFieldIds)] : ['/'],
            { queryParamsHandling: 'preserve' },
          );
        }),
      ),
    { dispatch: false },
  );

  getLoggedInEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetLoggedInEmployeeData),
      switchMap((_) =>
        this.api.getLoggedInEmployee().pipe(
          map((adviser: Employee) => new SetLoggedInEmployeeData(adviser)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getCrops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetCrops),
      switchMap(() =>
        this.cropApi.getCrops().pipe(
          map((crops) => new SetCrops(crops)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getNonProductiveCrops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetNonProductiveCrops),
      switchMap(() =>
        this.api.getNonProductiveCrops().pipe(
          map((crops) => new SetNonProductiveCrops(crops)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getCompanySettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetCompanySettings),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) =>
        this.arableCustomerService.getCompanySettings(cropYear).pipe(
          startWith(CoreActions.setLoading({ loading: true })),
          mergeMap((result) => {
            if (result && typeof result === 'object' && 'customerId' in result) {
              const actions: Action[] = [new SetCompanySettings(result)];
              if (result.yearlySettings?.showReminderForGrainRegistration) {
                actions.push(new ShowGrainRegistrationReminder(cropYear));
              }
              return of(...actions);
            }
            return of(result as Action);
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getSoilTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetSoilTypes),
      switchMap(() =>
        this.soilTypeApiService.getSoilTypes().pipe(
          map((st) => new SetSoilTypes(st)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getCropFieldAdvices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetCropFieldAdvices),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) =>
        this.api.getCropFieldAdvicesByCropYear(cropYear).pipe(
          map((cropFieldAdvices: CropFieldAdvice[]) => new SetCropFieldAdvices(cropFieldAdvices)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  updateGrasslandUsages$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateGrasslandUsages>(AppActionTypes.UpdateGrasslandUsages),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([parentEffect, cropYear]) =>
        this.api
          .updateGrasslandUsageForFertilizationSettings({
            grasslandUsage: parentEffect.grasslandUsage,
            year: cropYear,
          })
          .pipe(map(() => new GetFertilizationSettings())),
      ),
    ),
  );

  openPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenPlan),
      map((_) =>
        SessionActions.windowOpenWithSession({
          url: this.env.FIELD_PLAN_URL,
          name: '_self',
        }),
      ),
    ),
  );

  openOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenOrderPlanning),
      map((_) =>
        SessionActions.windowOpenWithSession({
          url: this.env.ORDERPLANNING_URL,
          name: '_self',
        }),
      ),
    ),
  );

  openFertilizerOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.OpenFertilizerOrderPlanning),
      map((_) =>
        SessionActions.windowOpenWithSession({
          url: `${this.env.ORDERPLANNING_URL}/fertilizerOrders`,
          name: '_self',
        }),
      ),
    ),
  );

  resetCropFieldSyncStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ResetCropFieldSyncStatus),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) => {
        const year = cropYear ?? new Date().getFullYear();

        return this.api.resetCropFieldSyncStatus(year).pipe(
          map(() => new GetCropFieldAdvices()),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        );
      }),
    ),
  );

  sendCancellationMail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.SendCancellationMail),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) => {
        const year = cropYear ?? new Date().getFullYear();
        return this.api.postSendCancellationMail(year).pipe(
          map(() => NotificationActions.successSnackbar({ message: 'Cancellation mail sent successfully' })),
          catchError((error) => {
            const validationText = this.translationHelpers.generateLocalisedValidationText(error.error, 'fertilizationBalance.validation');
            return of(NotificationActions.errorSnackbar({ message: validationText }));
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        );
      }),
    ),
  );

  getSkalReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AppActionTypes.GetSkalReport,
        AppActionTypes.LoadCustomerData,
        AdviceActionTypes.SaveTreatmentSuccess,
        AdviceActionTypes.SaveTreatmentsSuccess,
        AdviceActionTypes.SaveExecutedTreatmentSuccess,
        AdviceActionTypes.SaveFertilizationSettingSuccess,
        AdviceActionTypes.RemoveExecutedTreatmentSuccess,
        AdviceActionTypes.RemoveTreatmentSuccess,
        AdviceActionTypes.ClearAdviceConfirmedSuccess,
        AdviceActionTypes.CopyTreatmentsSuccess,
      ),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) => {
        const year = cropYear ?? new Date().getFullYear();

        return this.reportApi.getSkalReport(year).pipe(
          map((result) => new SetSkalReport(result)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        );
      }),
    ),
  );

  setExecutedTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.SaveExecutedTreatmentSuccess),
      withLatestFrom(this.store.pipe(select(selectEditingAdvice))),
      filter(([_, cropFieldAdvice]) => !!cropFieldAdvice),
      map(([_, cropFieldAdvice]) => new GetExecutedTreatmentForAdviceId(cropFieldAdvice.adviceId)),
    ),
  );

  clearExecutedTreatment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.RemoveExecutedTreatmentSuccess),
      withLatestFrom(this.store.pipe(select(selectEditingAdvice))),
      filter(([_, cropFieldAdvice]) => !!cropFieldAdvice),
      map(([_, cropFieldAdvice]) => new GetExecutedTreatmentForAdviceId(cropFieldAdvice.adviceId)),
    ),
  );

  getEditingAdviceFertilizationTableReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdviceActionTypes.SetEditingAdvice, AdviceActionTypes.SaveFertilizationSettingSuccess),
      withLatestFrom(
        this.store.pipe(select(selectEditingAdvice)),
        this.store.pipe(select(selectEditingTreatmentType)),
      ),
      filter(([_, cropFieldAdvice]) => !!cropFieldAdvice),
      switchMap(([_, cropFieldAdvice, treatmentType]) =>
        treatmentType === TreatmentType.Fertilization
          ? of(
            new GetFertilizationTableReport(
              [cropFieldAdvice.adviceId],
              [cropFieldAdvice.cropFieldId],
            ),
          )
          : EMPTY,
      ),
    ),
  );

  getFertilizationTableReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetFertilizationTableReport),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(
        ([{ cropFieldIds }, cropYear]: [
          { adviceIds: number[]; cropFieldIds: number[] },
          number,
        ]) => {
          const year = cropYear ?? new Date().getFullYear();
          return of(new GetRecommendedNutrientQuantityByAdviceIds(cropFieldIds, year));
        },
      ),
    ),
  );

  getOrganicMatterReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetOrganicMatterReport),
      withLatestFrom(
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(selectOrganicMatterReport)),
      ),
      switchMap(
        ([{ cropFieldIds }, cropYear]: [
          { cropFieldIds: number[] },
          number,
          OrganicMatterReport,
        ]) => {
          cropYear = cropYear ?? new Date().getFullYear();
          return this.organicMatterService.getOrganicMatterReport(cropYear, cropFieldIds).pipe(
            map((result) => new SetOrganicMatterReport(result)),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            catchError((_: HttpErrorResponse) =>
              of(
                NotificationActions.errorSnackbar({
                  message: this.locale.translate('organicMatterErrorMessage'),
                }),
              ),
            ),
          );
        },
      ),
    ),
  );

  getPlanetProofReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetPlanetProofReport),
      switchMap(({ cropYear, cropFieldIds }) =>
        this.planetProofService.getPlanetProofReport(cropYear, cropFieldIds).pipe(
          map((result) => new SetPlanetProofReport(result)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getEnvironmentalImpactReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetEnvironmentalImpactReport),
      switchMap(({ cropFieldIds }) =>
        this.environmentalImpactService.getEnvironmentalImpactReport(cropFieldIds).pipe(
          map((result) => new SetEnvironmentalImpactReport(result)),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  toggleReportSortOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleReportSortOption),
      withLatestFrom(this.store.pipe(select(getReportSort))),
      map(([_, reportSort]) => {
        const newReportSortOption =
          reportSort.option !== ReportSortOption.sortCropName
            ? ReportSortOption.sortCropName
            : ReportSortOption.sortCropFieldName;
        return new SetReportSort({
          option: newReportSortOption,
          descending: reportSort.descending,
        });
      }),
    ),
  );

  toggleReportSortDirection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleReportSortDirection),
      withLatestFrom(this.store.pipe(select(getReportSort))),
      map(
        ([_, reportSort]) =>
          new SetReportSort({
            option: reportSort.option,
            descending: !reportSort.descending,
          }),
      ),
    ),
  );

  saveReportSort$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.SetReportSort),
        withLatestFrom(this.store.pipe(select(selectSessionCustomerId))),
        map(([{ reportSort }, customerId]) => this.saveReportSort(customerId, reportSort)),
      ),
    { dispatch: false },
  );

  loadCustomerProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LoadCustomerProgress),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      mergeMap(([_, cropYear]) =>
        this.arableCustomerService.getCustomerProgress(cropYear).pipe(
          map((customerProgress) => new LoadCustomerProgressSuccess(customerProgress)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  toggleCustomerProgressGBM$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleCustomerProgressGBM),
      withLatestFrom(this.store.pipe(select(selectCustomerProgress))),
      mergeMap(([_, customerProgress]) =>
        this.arableCustomerService
          .upsertCustomerProgress({
            ...customerProgress,
            processes: {
              ...customerProgress.processes,
              fieldAdviceCropProtectionAdvicesCompleted:
                !customerProgress.processes.fieldAdviceCropProtectionAdvicesCompleted,
            },
          })
          .pipe(
            map(() => new LoadCustomerProgress()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  toggleCustomerProgressFertilization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ToggleCustomerProgressFertilization),
      withLatestFrom(this.store.pipe(select(selectCustomerProgress))),
      mergeMap(([_, customerProgress]) =>
        this.arableCustomerService
          .upsertCustomerProgress({
            ...customerProgress,
            processes: {
              ...customerProgress.processes,
              fieldAdviceFertilizationAdvicesCompleted:
                !customerProgress.processes.fieldAdviceFertilizationAdvicesCompleted,
            },
          })
          .pipe(
            map(() => new LoadCustomerProgress()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  showGrainRegistrationReminder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.ShowGrainRegistrationReminder),
      switchMap((action) =>
        this.dialog.dialog(
          GrainRegistrationReminderDialogComponent,
          {
            year: (action as ShowGrainRegistrationReminder).year,
            title: this.locale.translate('grainRegistrationReminder.title'),
            cancelButtonText: this.locale.translate('grainRegistrationReminder.dismiss'),
          },
        ).pipe(
          switchMap(() => of(new DisableGrainRegistrationReminder((action as ShowGrainRegistrationReminder).year))),
        ),
      ),
    ),
  );

  disableGrainRegistrationReminder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActionTypes.DisableGrainRegistrationReminder),
        switchMap((action: DisableGrainRegistrationReminder) =>
          this.arableCustomerService.disableGrainRegistrationReminder(action.year).pipe(
            tap(() => {
              this.store.dispatch(CoreActions.setLoading({ loading: false }));
            }),
            catchError((error) => {
              console.error('Error disabling grain registration reminder:', error);
              this.store.dispatch(CoreActions.setLoading({ loading: false }));
              return EMPTY;
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  private loadReportSort = (customerId: number): ReportSort => {
    const content = this.window.getCookie(this.getReportSortOptionCookieName(customerId));
    if (!content) {
      return { option: ReportSortOption.sortCropFieldName, descending: false };
    }

    const parts = content.split(' ');

    const option =
      ReportSortOption[
        (parts[0] as keyof typeof ReportSortOption) ?? ReportSortOption.sortCropFieldName
      ];
    const descending = parts[1] === 'DESC';

    return { option, descending };
  };

  private saveReportSort(customerId: number, reportSort: ReportSort): void {
    if (reportSort?.option) {
      this.window.setCookie(
        this.getReportSortOptionCookieName(customerId),
        reportSort.option.toString() + (reportSort.descending ? ' DESC' : ''),
        365 * 24 * 60 * 60 * 1000,
      );
    }
  }

  private getReportSortOptionCookieName = (customerId: number): string =>
    `customer-${customerId}-report-sort-option}`;
}
